import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogClose,
} from "@/components/ui/dialog";
import { useInquiry } from "@/contexts/InquiryContext";
import { apartments, getApartmentById } from "@/data/apartments";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  full_name: "",
  nationality: "",
  apartment_id: "",
  phone: "",
  email: "",
  message: "",
};

export default function InquiryModal() {
  const { open, presetApartmentId, setOpen, closeInquiry } = useInquiry();
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Reset form when modal opens; pre-fill apartment if provided
  useEffect(() => {
    if (open) {
      setForm({ ...emptyForm, apartment_id: presetApartmentId || "" });
      setErrors({});
      setSuccess(false);
    }
  }, [open, presetApartmentId]);

  const handleChange = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((er) => ({ ...er, [field]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.full_name.trim() || form.full_name.trim().length < 2)
      next.full_name = "Please enter your full name";
    if (!form.nationality.trim())
      next.nationality = "Please enter your nationality";
    if (!form.apartment_id) next.apartment_id = "Please select an apartment";
    if (!form.phone.trim() || form.phone.trim().length < 4)
      next.phone = "Please enter a valid phone number";
    if (!form.email.trim()) next.email = "Please enter your email address";
    else if (!/^\S+@\S+\.\S+$/.test(form.email))
      next.email = "Please enter a valid email address";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;

    const apt = getApartmentById(form.apartment_id);
    const apartmentName =
      form.apartment_id === "__consultancy__"
        ? "Consultancy Services"
        : apt
          ? `Case Study — ${apt.short_name}`
          : "Unspecified";
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        apartment_id: form.apartment_id,
        apartment_name: apartmentName,
        full_name: form.full_name.trim(),
        nationality: form.nationality.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
      });
      setSuccess(true);
      toast.success("Inquiry sent. Our team will be in touch shortly.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedApartment = getApartmentById(form.apartment_id);

  const inputBase =
    "w-full bg-[#F7F3EA] border rounded-lg px-4 py-3 text-sm text-[#2B3324] placeholder:text-[#5C6B4D]/50 transition-all outline-none focus:ring-1";
  const inputOk =
    "border-transparent focus:border-[#6E7F5B] focus:ring-[#6E7F5B]";
  const inputErr =
    "border-red-300 focus:border-red-400 focus:ring-red-300 bg-red-50/40";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogPortal>
        <DialogOverlay className="bg-[#2B3324]/40 backdrop-blur-sm" />
        <DialogContent
          data-testid="inquiry-modal"
          className="max-w-2xl w-[calc(100%-2rem)] p-0 overflow-hidden bg-[#F7F3EA] border-0 rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.25)] [&>button]:hidden max-h-[92vh] overflow-y-auto"
        >
          {success ? (
            <div className="p-8 md:p-12 text-center bg-white">
              <div
                data-testid="inquiry-modal-success"
                className="w-16 h-16 rounded-full bg-[#6E7F5B]/10 flex items-center justify-center mx-auto"
              >
                <CheckCircle2
                  className="w-8 h-8 text-[#6E7F5B]"
                  strokeWidth={1.5}
                />
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-light tracking-tight text-[#2B3324] mt-6">
                Thank you for your inquiry!
              </h3>
              <p className="mt-4 text-[#5C6B4D] leading-relaxed max-w-md mx-auto">
                Our team will contact you as soon as possible.
              </p>
              <button
                onClick={closeInquiry}
                data-testid="inquiry-modal-close-success"
                className="mt-8 bg-[#6E7F5B] hover:bg-[#5C6B4D] text-white px-8 py-3 rounded-lg font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5 shadow-sm"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <div className="relative bg-[#6E7F5B] text-white px-6 md:px-10 py-7">
                <DialogClose
                  data-testid="inquiry-modal-x"
                  className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-md flex items-center justify-center transition-colors"
                  aria-label="Close inquiry"
                >
                  <X className="w-4 h-4" />
                </DialogClose>
                <div className="text-[11px] tracking-[0.3em] uppercase text-[#F7F3EA]/80">
                  Kenneth Nathanael Lukman LLC — Consultancy
                </div>
                <h2 className="font-display text-2xl md:text-3xl font-light tracking-tight mt-2">
                  Request a Consultation
                </h2>
                <p className="mt-2 text-sm text-[#F7F3EA]/85 max-w-md">
                  Share a few details and our team will respond within one
                  business day. Every conversation stays confidential.
                </p>
              </div>

              <form
                onSubmit={handleSubmit}
                data-testid="inquiry-modal-form"
                className="p-6 md:p-10 bg-white space-y-5"
                noValidate
              >
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-[#5C6B4D] mb-2">
                    Full Name <span className="text-[#C5A059]">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.full_name}
                    onChange={handleChange("full_name")}
                    placeholder="Your full name"
                    data-testid="modal-input-fullname"
                    className={`${inputBase} ${errors.full_name ? inputErr : inputOk}`}
                  />
                  {errors.full_name && (
                    <p className="text-xs text-red-500 mt-1.5" data-testid="error-fullname">
                      {errors.full_name}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.15em] text-[#5C6B4D] mb-2">
                      Nationality <span className="text-[#C5A059]">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.nationality}
                      onChange={handleChange("nationality")}
                      placeholder="e.g. British"
                      data-testid="modal-input-nationality"
                      className={`${inputBase} ${errors.nationality ? inputErr : inputOk}`}
                    />
                    {errors.nationality && (
                      <p className="text-xs text-red-500 mt-1.5" data-testid="error-nationality">
                        {errors.nationality}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.15em] text-[#5C6B4D] mb-2">
                      Phone Number <span className="text-[#C5A059]">*</span>
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={handleChange("phone")}
                      placeholder="+44 ..."
                      data-testid="modal-input-phone"
                      className={`${inputBase} ${errors.phone ? inputErr : inputOk}`}
                    />
                    {errors.phone && (
                      <p className="text-xs text-red-500 mt-1.5" data-testid="error-phone">
                        {errors.phone}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-[#5C6B4D] mb-2">
                    Email Address <span className="text-[#C5A059]">*</span>
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={handleChange("email")}
                    placeholder="you@example.com"
                    data-testid="modal-input-email"
                    className={`${inputBase} ${errors.email ? inputErr : inputOk}`}
                  />
                  {errors.email && (
                    <p className="text-xs text-red-500 mt-1.5" data-testid="error-email">
                      {errors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-[#5C6B4D] mb-2">
                    Area of interest <span className="text-[#C5A059]">*</span>
                  </label>
                  <select
                    value={form.apartment_id}
                    onChange={handleChange("apartment_id")}
                    data-testid="modal-select-apartment"
                    className={`${inputBase} appearance-none bg-no-repeat bg-[right_1rem_center] pr-10 ${errors.apartment_id ? inputErr : inputOk}`}
                    style={{
                      backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%235C6B4D' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")",
                    }}
                  >
                    <option value="">Select an option…</option>
                    <option value="__consultancy__">
                      Consultancy Services (advisory · oversight · operator search)
                    </option>
                    <optgroup label="Case Studies · Similar Mandate">
                      {apartments.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.short_name} — {a.area}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                  {selectedApartment && (
                    <p className="text-xs text-[#5C6B4D] mt-1.5">
                      Case study · {selectedApartment.area}, London — discuss a similar advisory mandate
                    </p>
                  )}
                  {form.apartment_id === "__consultancy__" && (
                    <p className="text-xs text-[#5C6B4D] mt-1.5">
                      Independent property advisory · we&apos;ll respond within one business day
                    </p>
                  )}
                  {errors.apartment_id && (
                    <p className="text-xs text-red-500 mt-1.5" data-testid="error-apartment">
                      {errors.apartment_id}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-[11px] uppercase tracking-[0.15em] text-[#5C6B4D] mb-2">
                    Message{" "}
                    <span className="text-[#5C6B4D]/60 normal-case tracking-normal">
                      (optional)
                    </span>
                  </label>
                  <textarea
                    value={form.message}
                    onChange={handleChange("message")}
                    rows={4}
                    placeholder="Dates, group size, any special requests..."
                    data-testid="modal-input-message"
                    className={`${inputBase} ${inputOk} resize-none`}
                  />
                </div>

                <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
                  <button
                    type="button"
                    onClick={closeInquiry}
                    data-testid="modal-cancel"
                    className="sm:w-1/3 px-6 py-3.5 rounded-lg border border-[#E5E0D5] text-[#5C6B4D] hover:bg-[#F7F3EA] hover:border-[#6E7F5B] hover:text-[#2B3324] font-medium tracking-wide transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="modal-submit"
                    className="flex-1 bg-[#6E7F5B] hover:bg-[#5C6B4D] text-white py-3.5 rounded-lg font-medium tracking-wide transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 hover:-translate-y-0.5 shadow-sm"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Submit Inquiry
                      </>
                    )}
                  </button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
